import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import authService from '../../services/auth.service';
import { fullName } from '../../lib/fullName';
import { ROLE_LABEL, type Role } from '../../lib/roleRouting';
import TimeOffManager from '../scheduling/TimeOffManager';
import { colors, gradients, shadows, radii } from '../../styles/theme';
import {
  USER_MENU_PATHS,
  userMenuItems,
  type UserMenuItemKey,
  type UserMenuTone,
} from './userMenu.helpers';
import { getApiErrorMessage } from '../../lib/apiError';
import { LanguageChoice } from '../../i18n/LanguageChoice';
import type { Language } from '../../i18n/language';

const TONE_COLOR: Record<UserMenuTone, string> = {
  normal: colors.inkSoft,
  warning: colors.warning,
  danger: colors.danger,
};

/**
 * Avatar + dropdown rendered on the right of the topbar. Houses Profile,
 * Availability (agent-only), Days off (any staff), and the authoritative
 * Logout action (clears Zustand store AND localStorage tokens via
 * authService).
 *
 * Click-outside and Escape close the menu so it doesn't trap focus.
 *
 * Item hierarchy (revised 2026-05-24 after design review):
 *   Agent:   Profile → Availability → Days off → ─── → Sign out
 *   Manager: Profile → Days off → ─── → Sign out
 *   Client:  Profile → ─── → Sign out
 *
 * Why "Availability" is agent-only:
 *   "Availability" is the recurring weekly schedule used for client-
 *   appointment slot booking. Agents have one; managers don't (they
 *   don't take client appointments). The earlier draft of this menu
 *   showed an "Availability" item to managers that opened the
 *   TimeOffManager in mode="manager" (team-time-off oversight) — but
 *   the label was semantically wrong for managers (the surface was
 *   time-off, not availability). Cleaner to drop it; team-time-off
 *   management belongs on the manager dashboard, not the user menu.
 *
 * Days off opens TimeOffManager in mode="self" — quick single-day
 * absence add without navigating away. Hidden for clients.
 */
const UserMenu = (): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [daysOffOpen, setDaysOffOpen] = useState(false);
  const [languageError, setLanguageError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleLogout = (): void => {
    authService.logout();
    logout();
    navigate('/login', { replace: true });
  };

  const handleLanguage = (language: Language): void => {
    setLanguageError(null);
    authService.chooseMyLanguage(language).catch((err: unknown) => {
      setLanguageError(getApiErrorMessage(err, 'Your language could not be saved. Try again.'));
    });
  };

  const handleItem = (key: UserMenuItemKey): void => {
    if (key === 'sign_out') {
      handleLogout();
      return;
    }
    setOpen(false);
    if (key === 'days_off') {
      setDaysOffOpen(true);
      return;
    }
    const path = USER_MENU_PATHS[key];
    if (path) void navigate(path);
  };

  const email = user?.email ?? '—';
  const role = user?.role ?? '';
  // Staff see which account the window holds, not only its role: two staff accounts of one role
  // open side by side look identical otherwise, and an action meant for one gets done as the
  // other (2026-11-02). A client's bar stays as it was; their own menu names their address.
  const accountInBar = role !== '' && role !== 'client' && Boolean(user?.email);
  const displayName = fullName(user);
  const initial = (displayName || user?.email || 'U').charAt(0).toUpperCase();
  // Role-dependent items live in userMenu.helpers (pure + unit-tested). See
  // that file for the "Availability is agent-only / team time-off lives on
  // /manager/team (#66)" rationale.
  const menuItems = userMenuItems(role, user?.must_change_password === true);

  return (
    <div className="relative flex shrink-0 items-center gap-3" ref={wrapperRef}>
      {/* Always in view, left of the avatar (owner, 2026-10-25). The choice is kept with the
          account: the portal opens in it on any device. */}
      <div className="text-white/85">
        <LanguageChoice onChoose={handleLanguage} />
      </div>
      {languageError && (
        <p
          role="alert"
          className="absolute right-0 top-full mt-1 px-3 py-1.5 text-xs rounded whitespace-nowrap"
          style={{ background: colors.surface, color: colors.danger, boxShadow: shadows.menu }}
        >
          {languageError}
        </p>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2.5 pl-1.5 pr-3 py-1.5 rounded-full transition-colors"
        style={{ background: open ? 'rgba(255,255,255,0.06)' : 'transparent' }}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center font-semibold text-xs"
          style={{ background: gradients.gold, color: colors.navy }}
        >
          {initial}
        </div>
        {/* Role chip, and for staff the signed-in address under it, cut short with the whole
            address on hover. Its width is capped, as the nav measures the room left to it. Below
            480px the bar has no room left beside the language choice, and from 768 to 1023px the
            global search takes that room (e2e menus-fit): the open menu names the address there. */}
        {role && (
          <span
            className={`${accountInBar ? 'hidden min-[480px]:flex' : 'hidden md:flex'} min-w-0 flex-col items-start leading-tight`}
          >
            <span
              className="hidden md:inline text-[10px] uppercase tracking-wider whitespace-nowrap"
              style={{ color: colors.gold }}
            >
              {ROLE_LABEL[role as Role] ?? role}
            </span>
            {accountInBar && (
              <span
                translate="no"
                title={email}
                data-account="bar"
                className="block md:hidden lg:block max-w-[6rem] sm:max-w-[10rem] truncate text-[11px]"
                style={{ color: 'rgba(255,255,255,0.8)' }}
              >
                {email}
              </span>
            )}
          </span>
        )}
        <svg
          aria-hidden="true"
          className="w-3.5 h-3.5 text-white/50"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-64 overflow-hidden"
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radii.lg,
            boxShadow: shadows.menu,
          }}
        >
          <div className="px-4 py-3" style={{ borderBottom: `1px solid ${colors.borderSoft}` }}>
            <p className="text-sm font-semibold" style={{ color: colors.ink }}>
              {displayName || 'Signed in'}
            </p>
            <p
              translate="no"
              title={email}
              data-account="menu"
              className="text-xs truncate"
              style={{ color: colors.muted }}
            >
              {email}
            </p>
          </div>

          {/* Items are role-dependent (see userMenu.helpers). "Set password"
              replaces Profile when the account is flagged for forced rotation,
              so the menu describes what the click will actually do. */}
          {menuItems.map((item) => (
            <div
              key={item.key}
              style={item.dividerBefore ? { borderTop: `1px solid ${colors.borderSoft}` } : undefined}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => handleItem(item.key)}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between"
                style={{ color: TONE_COLOR[item.tone] }}
              >
                <span>{item.label}</span>
                {item.key === 'set_password' && (
                  <span
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                    style={{ background: colors.warningBg, color: colors.warning }}
                  >
                    Required
                  </span>
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Days off modal — opened from the menu, dismissed via the
          modal's own close affordance. Initial agent name pre-populates
          the header text but the modal internally re-resolves the UUID
          from /auth/me for the time-off API calls. */}
      {daysOffOpen && (
        <TimeOffManager
          mode="self"
          initialAgentName={displayName || email}
          onClose={() => setDaysOffOpen(false)}
        />
      )}

    </div>
  );
};

export default UserMenu;
